// ---------------------------------------------------------------------------
// Anschlussfehler über die Zeit (Roadmap AP-40 und AP-42): Prüfungen, die Zustand, Orte und Zeiten der Szenen zusammen lesen.
//
// - Reisezeit (AP-40): Eine Figur tritt erst an einem Ort auf und danach an einem anderen – dazwischen bleibt weniger Zeit, als der Weg
//   nach den Wegen der Bibliothek dauert (`relations.ts`, Art „weg“, auch über Zwischenorte).
// - Verletzung ohne Heilung (AP-42): verletzt, danach noch auftretend, aber nie wieder gesund (ein Hinweis, keine Gewissheit).
// - Merkmal im Text (AP-42): Der Text nennt neben einer Figur eine Ausprägung eines ihrer Merkmale, die zu der Zeit nicht gilt („die blonden
//   Haare“, obwohl sie seit Kapitel 3 grau sind) – gelesen wie das Alter im Text (Absatz mit Verweisen).
// - Benutzter Gegenstand (AP-42): Eine Figur benutzt in einer Handlung einen Gegenstand, der zu der Zeit bei jemand anderem ist, verloren ist
//   oder an einem anderen Ort liegt.
// ---------------------------------------------------------------------------

import type { TextRef } from './ageText.js';
import type { StoryCtx } from './ctx.js';
import { draftDocOf, narrativeOrder, worldOrder, worldSpanOf, type WorldSpan } from './narrative.js';
import { onStage } from './placeClash.js';
import { mentionsIn, refIndex } from './refs.js';
import { relationEntries, travelMinutes } from './relations.js';
import { isScene } from './scene.js';
import { placeOf } from './scenePlace.js';
import { spansIn } from './spans.js';
import { sceneBefore } from './stateConflicts.js';
import { changesOf, stateAt, stateEvents, stateRoleOf } from './states.js';
import type { Entity, IgnoredTerm, PriorSource, StoryNode } from './types.js';

const keyOf = (ctx: StoryCtx, id: string): string => ctx.entities.get(id)?.refKey ?? id;
const isIgnored = (ignored: readonly IgnoredTerm[] | undefined, entry: IgnoredTerm): boolean => !!ignored?.some((x) => x.term === entry.term && x.before === entry.before && x.after === entry.after);

// ---------------------------------------------------------------------------
// Reisezeit
// ---------------------------------------------------------------------------

/** Zu knapp für den Weg: \`entityId\` ist in \`fromSceneId\` in \`fromPlace\` und danach in \`sceneId\` in \`place\` – \`gap\` Minuten statt \`need\` */
export interface TravelConflict {
  entityId: string;
  sceneId: string;
  place: string;
  fromSceneId: string;
  fromPlace: string;
  need: number;
  gap: number;
}

/** „Ist Absicht“ (eine Abkürzung, Magie …): diese Figur auf dem Weg von dieser Szene her, gemerkt im Draft der späteren Szene */
export const travelIgnore = (ctx: StoryCtx, c: Pick<TravelConflict, 'entityId' | 'fromSceneId'>): IgnoredTerm => ({ term: '~reise', before: `@${keyOf(ctx, c.entityId)}`, after: c.fromSceneId });

/** Minuten je Einheit der Zeitrechnung; null: nicht vergleichbar */
const perMinute = (s: WorldSpan): number | null => (s.basis === 'ms' ? 60_000 : s.basis === 'count' ? 1 : null);
/** Wie spät eine Szene höchstens beginnt (Minuten): Genauigkeit Tag bzw. Stunde lässt den Rest offen; ungefähr, Monat, Jahr: gar nicht */
const slack = (n: StoryNode): number | null => {
  if (n.time?.approx || n.time?.precision === 'month' || n.time?.precision === 'year') return null;
  return n.time?.precision === 'day' ? 1440 : n.time?.precision === 'hour' ? 60 : 0;
};

const travelCache = new WeakMap<StoryCtx, ReadonlyMap<string, TravelConflict[]>>();

/**
 * Alle Wege, die zu knapp sind, je Szene (an der späteren): je Figur ihre Auftritte (Perspektive, Rede, Handlung, Gedanke) an einem
 * Schauplatz in Weltzeit, je zwei aufeinanderfolgende an verschiedenen Orten. Zeit zwischen ihnen: vom Ende der ersten bis zum spätest
 * möglichen Beginn der zweiten; überschneiden sie sich, ist das „zwei Orte zugleich“. Ohne Weg zwischen den Orten: nichts.
 */
export function travelConflicts(ctx: StoryCtx): ReadonlyMap<string, TravelConflict[]> {
  const cached = travelCache.get(ctx);
  if (cached) return cached;
  const out = new Map<string, TravelConflict[]>();
  travelCache.set(ctx, out);
  if (!relationEntries(ctx).some((x) => x.relation.kind === 'weg')) return out;
  const refs = refIndex(ctx);
  const byFigure = new Map<string, { scene: StoryNode; place: string; span: WorldSpan; unit: number; slack: number }[]>();
  for (const scene of ctx.story.nodes.filter(isScene)) {
    const place = placeOf(ctx, scene);
    const span = worldSpanOf(ctx, scene);
    const unit = span ? perMinute(span) : null;
    const s = slack(scene);
    if (!place || !span || unit === null || s === null) continue;
    for (const id of onStage(ctx, scene, refs)) {
      const list = byFigure.get(id) ?? [];
      list.push({ scene, place, span, unit, slack: s });
      byFigure.set(id, list);
    }
  }
  for (const [entityId, list] of byFigure) {
    const sorted = list.filter((x) => x.span.basis === list[0]!.span.basis).sort((a, b) => a.span.start - b.span.start || a.span.end - b.span.end);
    for (let i = 1; i < sorted.length; i++) {
      const a = sorted[i - 1]!;
      const b = sorted[i]!;
      if (a.place === b.place) continue;
      const gap = (b.span.start - a.span.end) / a.unit + b.slack;
      if (b.span.start < a.span.end) continue;
      const need = travelMinutes(ctx, a.place, b.place);
      if (need === null || gap >= need) continue;
      if (isIgnored(draftDocOf(b.scene)?.ignored, travelIgnore(ctx, { entityId, fromSceneId: a.scene.id }))) continue;
      const c: TravelConflict = { entityId, sceneId: b.scene.id, place: b.place, fromSceneId: a.scene.id, fromPlace: a.place, need, gap: Math.max(0, Math.round(gap)) };
      out.set(c.sceneId, [...(out.get(c.sceneId) ?? []), c]);
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// Verletzung ohne Heilung
// ---------------------------------------------------------------------------

/** Eine Figur, verletzt in \`sceneId\`, tritt danach noch in \`later\` Szenen auf, wird aber nie wieder gesund */
export interface OpenInjury {
  entityId: string;
  sceneId: string;
  later: number;
}

/** „Ist Absicht“ (die Wunde bleibt): für diese Figur, gemerkt im Draft der Szene der Verletzung */
export const injuryIgnore = (ctx: StoryCtx, entityId: string): IgnoredTerm => ({ term: '~heilung', before: `@${keyOf(ctx, entityId)}`, after: '' });

/**
 * Verletzungen ohne Heilung: die erste Verletzung seit der letzten Genesung (bzw. Geburt), wenn danach weder „gesund“ noch „tot“ folgt und
 * die Figur in Weltzeit danach noch auftritt (Perspektive, Rede, Handlung, Gedanke).
 */
export function openInjuries(ctx: StoryCtx): OpenInjury[] {
  const open = new Map<string, { sceneId: string; world: number }>();
  for (const e of stateEvents(ctx)) {
    const c = e.change;
    if (c.kind !== 'verfassung') continue;
    if (c.value === 'verletzt') {
      if (!open.has(c.entityId)) open.set(c.entityId, { sceneId: e.sceneId, world: e.world });
    } else open.delete(c.entityId);
  }
  if (open.size === 0) return [];
  const refs = refIndex(ctx);
  const scenes = worldOrder(ctx, ctx.story.nodes.filter(isScene));
  const out: OpenInjury[] = [];
  for (const [entityId, at] of open) {
    if (stateRoleOf(ctx, entityId) !== 'person') continue;
    const later = scenes.filter((s, i) => i > at.world && onStage(ctx, s, refs).has(entityId)).length;
    const scene = ctx.nodes.get(at.sceneId);
    if (later === 0 || !scene || isIgnored(draftDocOf(scene)?.ignored, injuryIgnore(ctx, entityId))) continue;
    out.push({ entityId, sceneId: at.sceneId, later });
  }
  return out;
}

// ---------------------------------------------------------------------------
// Merkmal im Text
// ---------------------------------------------------------------------------

/** Eine Ausprägung im Text, die zu der Zeit nicht gilt: genannt \`said\`, es gilt \`actual\` (seit \`sinceSceneId\`) */
export interface TraitConflict {
  from: number;
  to: number;
  entityId: string;
  trait: string;
  said: string;
  actual: string;
  sinceSceneId: string;
  /** seit einer früheren Episode der Serie (`sinceSceneId` leer) */
  prior?: PriorSource;
}

/** „Ist Absicht“ (eine Perücke, eine Erinnerung …): diese Ausprägung bei dieser Figur, gemerkt im Dokument */
export const traitIgnore = (ctx: StoryCtx, c: Pick<TraitConflict, 'entityId' | 'said'>): IgnoredTerm => ({ term: '~merkmal', before: `@${keyOf(ctx, c.entityId)}`, after: c.said.toLowerCase() });

const traitCache = new WeakMap<StoryCtx, Map<string, Map<string, Set<string>>>>();
/** Je Figur und Merkmal alle Ausprägungen, die es irgendwann hat (klein geschrieben) */
function traitValues(ctx: StoryCtx): Map<string, Map<string, Set<string>>> {
  let out = traitCache.get(ctx);
  if (out) return out;
  out = new Map();
  const add = (entityId: string, trait: string, value: string): void => {
    const byTrait = out!.get(entityId) ?? new Map<string, Set<string>>();
    const key = trait.trim().toLowerCase();
    byTrait.set(key, (byTrait.get(key) ?? new Set()).add(value.trim().toLowerCase()));
    out!.set(entityId, byTrait);
  };
  // aus früheren Episoden der Serie: die Ausprägung am Ende der vorigen
  for (const p of ctx.story.series?.entities ?? []) for (const t of p.traits ?? []) add(p.entityId, t.trait, t.value);
  for (const n of ctx.story.nodes)
    for (const c of changesOf(n)) {
      if (c.kind !== 'merkmal' || !c.trait || c.value === null) continue;
      add(c.entityId, c.trait, c.value);
    }
  traitCache.set(ctx, out);
  return out;
}

const escape = (s: string): string => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Ausprägungen im Text (Absatz mit seinen Verweisen), die zur Zeit der Szene nicht gelten: im selben Satz wie der Verweis auf die Figur eine
 * andere Ausprägung eines ihrer Merkmale (als Wortanfang: „blond“ trifft „blonden“). Nur Merkmale, deren Ausprägung vor der Szene feststeht
 * und sich in der Szene nicht ändert.
 */
export function traitTextConflicts(ctx: StoryCtx, scene: StoryNode, text: string, refs: readonly TextRef[], ignored?: readonly IgnoredTerm[], index: ReadonlyMap<string, Entity> = refIndex(ctx)): TraitConflict[] {
  const values = traitValues(ctx);
  if (values.size === 0 || refs.length === 0) return [];
  const out: TraitConflict[] = [];
  const seen = new Set<string>();
  for (const r of refs) {
    const e = index.get(r.key);
    const byTrait = e ? values.get(e.id) : undefined;
    if (!e || !byTrait) continue;
    const at = stateAt(ctx, e.id, scene.id);
    const changedHere = new Set(at.here.filter((c) => c.kind === 'merkmal' && c.trait).map((c) => c.trait!.trim().toLowerCase()));
    // der Satz um den Verweis
    const start = Math.max(...['.', '!', '?', '\n'].map((p) => text.lastIndexOf(p, r.from - 1))) + 1;
    const ends = ['.', '!', '?', '\n'].map((p) => text.indexOf(p, r.to)).filter((i) => i >= 0);
    const end = ends.length > 0 ? Math.min(...ends) : text.length;
    const sentence = text.slice(start, end);
    for (const t of at.before.traits) {
      const key = t.trait.toLowerCase();
      if (changedHere.has(key)) continue;
      const actual = t.value.trim().toLowerCase();
      for (const v of byTrait.get(key) ?? []) {
        if (v === actual || actual.includes(v)) continue;
        const m = new RegExp(`(?<![\\p{L}\\p{N}])${escape(v)}`, 'iu').exec(sentence);
        if (!m) continue;
        const from = start + m.index;
        if (seen.has(`${from}`) || isIgnored(ignored, traitIgnore(ctx, { entityId: e.id, said: v }))) continue;
        seen.add(`${from}`);
        out.push({ from, to: from + m[0].length, entityId: e.id, trait: t.trait, said: m[0], actual: t.value, sinceSceneId: t.sceneId, ...(t.prior ? { prior: t.prior } : {}) });
      }
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// Benutzter Gegenstand
// ---------------------------------------------------------------------------

/** \`personId\` benutzt \`itemId\`, der zu der Zeit bei \`holder\` ist (null: verloren) – seit \`sinceSceneId\` (leer: laut Feld „Besitzer“) */
export interface ItemUseConflict {
  personId: string;
  itemId: string;
  holder: string | null;
  sinceSceneId: string;
  /** seit einer früheren Episode der Serie (`sinceSceneId` leer) */
  prior?: PriorSource;
}

/** „Ist Absicht“ (geliehen, heimlich genommen …): dieser Gegenstand bei dieser Figur, gemerkt im Dokument */
export const itemUseIgnore = (ctx: StoryCtx, c: Pick<ItemUseConflict, 'personId' | 'itemId'>): IgnoredTerm => ({ term: '~benutzt', before: `@${keyOf(ctx, c.itemId)}`, after: `@${keyOf(ctx, c.personId)}` });

const readingCache = new WeakMap<StoryCtx, Map<string, number>>();
const readingOf = (ctx: StoryCtx): Map<string, number> => {
  let m = readingCache.get(ctx);
  if (!m) readingCache.set(ctx, (m = new Map(narrativeOrder(ctx).scenes.map((s, i) => [s.scene.id, i]))));
  return m;
};
const eventsCache = new WeakMap<StoryCtx, ReturnType<typeof stateEvents>>();
const eventsOf = (ctx: StoryCtx): ReturnType<typeof stateEvents> => {
  let all = eventsCache.get(ctx);
  if (!all) eventsCache.set(ctx, (all = stateEvents(ctx)));
  return all;
};

/**
 * Hat \`personId\` den Gegenstand \`itemId\` zur Zeit von \`scene\`? null: ja bzw. nicht bestimmbar (unbekannt, bei wem er ist; er wechselt in
 * der Szene den Besitzer; die Szene, seit der er woanders ist, liegt nicht sicher davor); sonst, wo er ist. An einem Ort: in Ordnung, wenn
 * die Szene dort spielt.
 */
export function itemUseConflict(ctx: StoryCtx, scene: StoryNode, personId: string, itemId: string): ItemUseConflict | null {
  if (stateRoleOf(ctx, itemId) !== 'item' || stateRoleOf(ctx, personId) !== 'person') return null;
  if (changesOf(scene).some((c) => c.kind === 'besitz' && c.entityId === itemId)) return null;
  const holder = stateAt(ctx, itemId, scene.id, eventsOf(ctx)).before.holder;
  if (!holder || holder.value === personId) return null;
  if (holder.value !== null && stateRoleOf(ctx, holder.value) === 'place' && placeOf(ctx, scene) === holder.value) return null;
  if (holder.sceneId) {
    const since = ctx.nodes.get(holder.sceneId);
    if (!since || sceneBefore(ctx, since, scene, readingOf(ctx)) !== 'sure') return null;
  }
  return { personId, itemId, holder: holder.value, sinceSceneId: holder.sceneId, ...(holder.prior ? { prior: holder.prior } : {}) };
}

/** Wo der Draft einer Szene (Markdown) Gegenstände benutzt, die die Figur da nicht hat: Handlungen einer Person mit dem Gegenstand im Bezug */
export function itemUseConflicts(ctx: StoryCtx, scene: StoryNode, text: string, ignored?: readonly IgnoredTerm[]): (ItemUseConflict & { index: number })[] {
  if (!text.includes(']{@')) return [];
  const refs = refIndex(ctx);
  const out = new Map<string, ItemUseConflict & { index: number }>();
  for (const s of spansIn(text)) {
    if (s.role !== 'handlung') continue;
    const ids = s.keys.map((k) => refs.get(k)?.id).filter((id): id is string => !!id);
    const inner = mentionsIn(text.slice(s.textFrom, s.textTo)).map((m) => refs.get(m.key)?.id);
    const people = ids.filter((id) => stateRoleOf(ctx, id) === 'person');
    const items = [...new Set([...ids, ...inner].filter((id): id is string => !!id && stateRoleOf(ctx, id) === 'item'))];
    for (const p of people)
      for (const i of items) {
        if (out.has(`${p}:${i}`) || isIgnored(ignored, itemUseIgnore(ctx, { personId: p, itemId: i }))) continue;
        const c = itemUseConflict(ctx, scene, p, i);
        if (c) out.set(`${p}:${i}`, { ...c, index: s.from });
      }
  }
  return [...out.values()];
}
